// The red line under a product tile: when a buyer can put something on an order but
// nothing ships today, say when it does ship. Two mutually exclusive cases —
//
//   stock landing on a known PO date  -> "Available 08/26/2026"
//   a prebook is the only supply      -> "Prebook: Start Ship 2/25/27"
//
// The second case exists because productOrderable's canOrderProduct() counts a prebook
// as reason enough to keep the Order button live, while the PO dates come only from
// dated quantity rows. Without it a prebook-only tile is orderable and completely
// silent about the wait, which is how a buyer ends up ordering something that ships
// months out. The quick view has always shown this date (QuantityInput.vue); the tiles
// had not.
//
// Host component must also use the productOrderable mixin and expose activeDraftOrder.

const DATE_OPTIONS = { month: '2-digit', day: '2-digit', year: 'numeric' }

export default {
  methods: {
    availabilityNotice(product) {
      const quantities = product?.product_date_quantities || []

      if (this.isAvailableQuantity(quantities)) {
        const [poDate] = this.getPODates(quantities)
        if (poDate && this.landsAfterShipDate(poDate)) {
          return `Available\u00a0${poDate}`
        }
      }

      if (this.showPrebookStartShip(product)) {
        return `Prebook: Start Ship\u00a0${product.prebook_date_formatted}`
      }

      return null
    },

    showPrebookStartShip(product) {
      if (!this.productPrebookActive(product) || !product.prebook_date_formatted) return false
      // Anything on hand or already incoming is the "Available" line's job, and its
      // presence is exactly what makes getPODates() non-empty — so the two never collide.
      if (this.productHasAvailableQty(product)) return false
      return this.landsAfterShipDate(product.prebook_date)
    },

    // A date the buyer is actually waiting on. Once the order's start ship date reaches
    // it the goods are expected in — for a prebook that is the bypass rule in
    // PrebookBypassTrait — and the warning would be noise.
    landsAfterShipDate(date) {
      return (
        !this.activeDraftOrder ||
        new Date(this.activeDraftOrder.start_ship_date) < new Date(date)
      )
    },

    getPODates(data) {
      const currentDate = new Date()
      let uniqueDatesSet = new Set()
      let defaultCounter = 0
      let counter = 0
      data.forEach((entry) => {
        const date = new Date(entry.purchased_order_available_at)
        if (date > currentDate) {
          // Check if the quantities string contains multiple values separated by commas
          if (entry.quantities.includes(',')) {
            // Convert quantities to an array of numbers and check if all are greater than 0
            const quantitiesArray = entry.quantities.split(',').map(Number)
            if (quantitiesArray.every((qty) => qty > 0)) {
              counter++
              uniqueDatesSet.add(date.toLocaleDateString('en-US', DATE_OPTIONS))
            }
          } else if (Number(entry.quantities) > 0) {
            // Handle single quantity
            counter++
            uniqueDatesSet.add(date.toLocaleDateString('en-US', DATE_OPTIONS))
          }

          defaultCounter++
        }
      })
      if (defaultCounter !== counter) {
        uniqueDatesSet = new Set()
      }

      return Array.from(uniqueDatesSet)
    },

    isAvailableQuantity(data) {
      return data.some((item) => {
        if (item.quantities.includes(',')) {
          // Handle cases with multiple quantities
          return item.quantities.split(',').every((qty) => Number(qty) === 0)
        } else {
          // Handle single quantity
          return Number(item.quantities) === 0
        }
      })
    }
  }
}
